import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment,
  Alert, CircularProgress, IconButton, Menu, Tooltip, FormHelperText,
  Stack, useMediaQuery, useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import { getUsers, updateUserRole, createUser, getDoctors, adminResetPassword, resendVerification, adminForceVerifyEmail } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';

// description: texto corto de ayuda mostrado debajo del selector de rol —
// antes no había ninguna explicación en la app, solo en MANUAL.md (que el
// usuario en general no llega a leer). No reemplaza la tabla comparativa
// completa del manual, solo resuelve la duda más común al crear un usuario.
const ROLES = [
  {
    value: 'admin', label: 'Admin',
    description: 'Acceso total: gestiona usuarios, médicos, configuración y ve toda la historia clínica. Si sos el único usuario del consultorio (ej. médico dueño que también hace de recepción), "Admin" te alcanza para hacer todo — vas a elegir tu propio nombre como médico en cada turno/consulta/receta.',
  },
  {
    value: 'recepcionista', label: 'Recepcionista',
    description: 'Agenda, pacientes y facturación — sin acceso a historia clínica, recetas ni configuración de la clínica.',
  },
  {
    value: 'medico', label: 'Médico',
    description: 'Ve y carga historia clínica, recetas y órdenes — limitado a sus propios turnos y consultas. No gestiona usuarios ni configuración.',
  },
  {
    value: 'invitado', label: 'Invitado (solo Algoritmos)',
    description: 'Acceso externo de solo lectura a los árboles de decisión publicados (ej. un colega de otra institución) — no ve pacientes, turnos ni nada más del sistema.',
  },
];

const ROLE_COLORS = { admin: 'error', recepcionista: 'primary', medico: 'success', invitado: 'default' };

const emptyCreate = { username: '', password: '', email: '', role: 'recepcionista', doctor_id: '' };

const UsersAdmin = () => {
  const { user: me, clinicId } = useAuth();
  const notify = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [selected, setSelected] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editForm, setEditForm] = useState({ role: '', doctor_id: '' });
  const [resetPassword, setResetPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Lista chica por naturaleza (staff de una clínica, no pacientes/turnos) —
  // alcanza con filtrar client-side sobre lo ya cargado, sin paginar server-side.
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(u =>
      u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const load = async () => {
    setLoading(true);
    try {
      const [ur, dr] = await Promise.all([getUsers(), getDoctors({ limit: 500 })]);
      setUsers(ur.data || []);
      setDoctors(dr.data?.items || []);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setCreate = (f) => (e) => setCreateForm(p => ({ ...p, [f]: e.target.value }));
  const setEdit = (f) => (e) => setEditForm(p => ({ ...p, [f]: e.target.value }));

  const handleCreate = async () => {
    if (!createForm.username || !createForm.password) {
      setError('Usuario y contraseña son obligatorios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await createUser({
        username: createForm.username,
        password: createForm.password,
        email: createForm.email || undefined,
        role: createForm.role,
        clinic_id: clinicId,
        doctor_id: createForm.role === 'medico' && createForm.doctor_id ? Number(createForm.doctor_id) : null,
      });
      setOpenCreate(false);
      setCreateForm(emptyCreate);
      await load();
      if (res.data?.email && !res.data?.email_verified) {
        notify(`Le enviamos un email de confirmación a ${res.data.email} — no va a poder iniciar sesión hasta confirmarlo.`, 'info');
      } else {
        notify('Usuario creado', 'success');
      }
    } catch (err) {
      setError(err.userMessage || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const openMenu = (e, u) => { setMenuAnchor(e.currentTarget); setMenuUser(u); };
  const closeMenu = () => { setMenuAnchor(null); setMenuUser(null); };

  const handleOpenEdit = (u) => {
    closeMenu();
    setSelected(u);
    setEditForm({ role: u.role, doctor_id: u.doctor_id || '' });
    setError('');
    setOpenEdit(true);
  };

  const handleOpenReset = (u) => {
    closeMenu();
    setSelected(u);
    setResetPassword('');
    setError('');
    setOpenReset(true);
  };

  const handleResendVerification = async (u) => {
    closeMenu();
    try {
      await resendVerification(u.email);
      notify(`Reenviamos el link de confirmación a ${u.email}`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al reenviar el email', 'error');
    }
  };

  const handleForceVerify = async (u) => {
    closeMenu();
    try {
      await adminForceVerifyEmail(u.id);
      notify(`Cuenta de ${u.username} confirmada`, 'success');
      await load();
    } catch (err) {
      notify(err.userMessage || 'Error al confirmar la cuenta', 'error');
    }
  };

  const handleDeactivate = async (u) => {
    closeMenu();
    if (!window.confirm(`¿Dar de baja a ${u.username}? No va a poder iniciar sesión ni aparecer en nuevas asignaciones, pero todo lo que ya cargó (turnos, historia clínica, auditoría) queda intacto.`)) return;
    try {
      await updateUserRole(u.id, { is_active: false });
      notify('Usuario dado de baja', 'success');
      await load();
    } catch (err) {
      notify(err.userMessage || 'Error al dar de baja el usuario', 'error');
    }
  };

  const handleReactivate = async (u) => {
    closeMenu();
    try {
      await updateUserRole(u.id, { is_active: true });
      notify('Usuario reactivado', 'success');
      await load();
    } catch (err) {
      notify(err.userMessage || 'Error al reactivar el usuario', 'error');
    }
  };

  const handleReset = async () => {
    if (resetPassword.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    setSaving(true); setError('');
    try {
      await adminResetPassword(selected.id, resetPassword);
      setOpenReset(false);
    } catch (err) {
      setError(err.userMessage || 'Error al resetear contraseña');
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    setError('');
    try {
      await updateUserRole(selected.id, {
        role: editForm.role,
        doctor_id: editForm.role === 'medico' && editForm.doctor_id ? Number(editForm.doctor_id) : null,
      });
      setOpenEdit(false);
      await load();
    } catch (err) {
      setError(err.userMessage || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Usuarios</Typography>
        <Button variant="contained" onClick={() => { setCreateForm(emptyCreate); setError(''); setOpenCreate(true); }}>
          Nuevo usuario
        </Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          placeholder="Buscar por usuario o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
        />
      </Paper>

      {filteredUsers.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'Sin usuarios que coincidan con la búsqueda' : 'Sin usuarios'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filteredUsers.map(u => {
            const doctor = doctors.find(d => d.id === u.doctor_id);
            return (
              <Paper key={u.id} sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>{u.username}</Typography>
                      {u.id === me?.id && <Chip label="Vos" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
                      <Chip label={u.role} color={ROLE_COLORS[u.role] || 'default'} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                      {u.is_active === false && (
                        <Chip label="Dado de baja" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                      )}
                    </Box>
                    {u.email && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {u.email}
                        {!u.email_verified && (
                          <Chip label="Sin confirmar" size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Typography>
                    )}
                    {doctor && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Médico: {doctor.name}</Typography>}
                  </Box>
                  <IconButton size="small" onClick={(e) => openMenu(e, u)} sx={{ flexShrink: 0 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Médico vinculado</TableCell>
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map(u => {
                const doctor = doctors.find(d => d.id === u.doctor_id);
                return (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      {u.username}
                      {u.id === me?.id && (
                        <Chip label="Vos" size="small" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />
                      )}
                      {u.is_active === false && (
                        <Chip label="Dado de baja" size="small" color="default" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {u.email || '-'}
                      {u.email && !u.email_verified && (
                        <Chip
                          label="Sin confirmar"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.role}
                        color={ROLE_COLORS[u.role] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{doctor ? doctor.name : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Acciones">
                        <IconButton size="small" onClick={(e) => openMenu(e, u)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Menú de acciones */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => handleOpenEdit(menuUser)}>Editar rol</MenuItem>
        <MenuItem dense sx={{ color: 'warning.main' }} onClick={() => handleOpenReset(menuUser)}>
          Resetear contraseña
        </MenuItem>
        {menuUser?.email && !menuUser?.email_verified && (
          <MenuItem dense onClick={() => handleResendVerification(menuUser)}>
            Reenviar confirmación
          </MenuItem>
        )}
        {menuUser?.email && !menuUser?.email_verified && (
          <MenuItem dense sx={{ color: 'success.main' }} onClick={() => handleForceVerify(menuUser)}>
            Confirmar manualmente
          </MenuItem>
        )}
        {menuUser?.is_active === false ? (
          <MenuItem dense sx={{ color: 'success.main' }} onClick={() => handleReactivate(menuUser)}>
            Reactivar
          </MenuItem>
        ) : (
          menuUser?.id !== me?.id && (
            <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDeactivate(menuUser)}>
              Dar de baja
            </MenuItem>
          )
        )}
      </Menu>

      {/* Dialog crear */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nuevo usuario</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre de usuario *" fullWidth value={createForm.username} onChange={setCreate('username')}
            error={!createForm.username && !!error} />
          <TextField label="Contraseña *" type="password" fullWidth value={createForm.password} onChange={setCreate('password')}
            error={!createForm.password && !!error} />
          <TextField label="Email" fullWidth value={createForm.email} onChange={setCreate('email')} />
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select value={createForm.role} label="Rol" onChange={setCreate('role')}>
              {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
            <FormHelperText sx={{ mx: 0 }}>
              {ROLES.find(r => r.value === createForm.role)?.description}
            </FormHelperText>
          </FormControl>
          {createForm.role === 'medico' && (
            <FormControl fullWidth>
              <InputLabel>Médico vinculado</InputLabel>
              <Select value={createForm.doctor_id} label="Médico vinculado" onChange={setCreate('doctor_id')}>
                <MenuItem value=""><em>Sin vincular</em></MenuItem>
                {doctors.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog editar rol */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar rol — {selected?.username}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select value={editForm.role} label="Rol" onChange={setEdit('role')}>
              {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
            <FormHelperText sx={{ mx: 0 }}>
              {ROLES.find(r => r.value === editForm.role)?.description}
            </FormHelperText>
          </FormControl>
          {editForm.role === 'medico' && (
            <FormControl fullWidth>
              <InputLabel>Médico vinculado</InputLabel>
              <Select value={editForm.doctor_id} label="Médico vinculado" onChange={setEdit('doctor_id')}>
                <MenuItem value=""><em>Sin vincular</em></MenuItem>
                {doctors.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog resetear contraseña */}
      <Dialog open={openReset} onClose={() => setOpenReset(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resetear contraseña — {selected?.username}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning" sx={{ py: 0.5 }}>
            El usuario deberá usar esta nueva contraseña para iniciar sesión.
          </Alert>
          <TextField
            label="Nueva contraseña *"
            type="password"
            fullWidth
            value={resetPassword}
            onChange={e => setResetPassword(e.target.value)}
            error={resetPassword.length > 0 && resetPassword.length < 8 && !!error}
            helperText={error || "Mínimo 8 caracteres"}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReset(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleReset} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Resetear contraseña'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersAdmin;
